import React, { useState } from 'react';
import { Card, Grid, Pagination, styled, useMediaQuery } from '@mui/material';
import { Text } from 'library';
import { useClient } from 'shared';
import { AvailableTeacher, PagingResult } from 'models';
import AvailableTeacherCard from './AvailableTeacherCard';
import AvailableTeachersCardGrid from './AvailableTeachersCardGrid';
import EditOrCreateNewJobModal from './EditOrCreateNewJobModal';
import { useModalsContext } from 'ModalsContext';
import moment from 'moment-timezone';
import theme from 'theme';
import {
    teacherToSpotlightWorkerApiObject,
    timeslotToDaysOfWeekApiObject,
    timeslotToJobTimesApiObject,
} from './inviteUtils';
import ViewMoreTeachers from './ViewMoreTeachers';

export default function AvailableTeachers({ small }: { small: boolean }) {
    const [page, setPage] = useState(1);
    const xlUp = useMediaQuery(theme.breakpoints.up('xl'));
    const pageSize = xlUp ? 3 : 2;
    const { data: availableTeachers } = useClient<PagingResult<AvailableTeacher>>({
        url: `api/available-teachers/?page_size=${pageSize}&page=${page}`,
    });
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState<AvailableTeacher[]>([]);
    const [selectedTimeslot, setSelectedTimeslot] = useState<{ upper: string; lower: string; timezone: string }>();

    const { openJobForm } = useModalsContext();

    function openInviteModal(teacher: AvailableTeacher, lower: string, upper: string, timezone?: string) {
        setSelectedWorker([teacher]);
        setSelectedTimeslot({ lower, upper, timezone: timezone ?? 'America/New_York' });
        setInviteModalOpen(true);
    }

    function closeAndReset() {
        setInviteModalOpen(false);
        setSelectedWorker([]);
        setSelectedTimeslot(undefined);
    }

    function request() {
        if (!selectedTimeslot || !selectedWorker) {
            return;
        }
        const formattedWorker = teacherToSpotlightWorkerApiObject(selectedWorker[0]);

        setSelectedWorker([]);

        openJobForm('Available Teachers', {
            selectedSpotlightWorkers: [formattedWorker],
            allSpotlightWorkers: [formattedWorker],
            startDate: moment(selectedTimeslot?.lower),
            endDate: moment(selectedTimeslot?.upper),
            daysOfTheWeek: timeslotToDaysOfWeekApiObject(selectedTimeslot),
            jobTimes: timeslotToJobTimesApiObject(selectedTimeslot),
        });
    }

    return (
        <Container small={small}>
            <Text variant="h1">Book Available Teachers</Text>
            <Text textStyle={{ marginTop: 10, marginBottom: 20 }} variant="body1">
                Request teachers who&apos;ve marked availability for this day. They&apos;ll receive a direct request and
                must confirm before the shift is booked.
            </Text>
            {availableTeachers?.results?.length ? (
                <>
                    <AvailableTeachersCardGrid
                        width="600px"
                        renderItem={(teacher: AvailableTeacher) => (
                            <AvailableTeacherCard key={teacher.id} teacher={teacher} requestToBook={openInviteModal} />
                        )}
                        items={availableTeachers?.results || []}
                    />
                    <Grid container justifyContent="space-between">
                        <ViewMoreTeachers />
                        {(availableTeachers?.num_pages || 0) > 1 && (
                            <Pagination
                                count={availableTeachers?.num_pages || 0}
                                page={page}
                                onChange={(event, page) => setPage(page)}
                                style={{ marginTop: 20 }}
                            />
                        )}
                    </Grid>
                    <EditOrCreateNewJobModal
                        open={inviteModalOpen}
                        onClose={closeAndReset}
                        selectedWorkers={selectedWorker}
                        openJobForm={request}
                    />
                </>
            ) : (
                <Grid container justifyContent="center" gap={4}>
                    <Text variant="body1" bold>
                        We didn&apos;t find any teachers who entered availability near you, but you can still invite
                        teachers from the Teacher Showcase.
                    </Text>
                    <ViewMoreTeachers />
                </Grid>
            )}
        </Container>
    );
}

const Container = styled(Card)(({ small }: { small: boolean }) => ({
    padding: 20,
    marginTop: 20,
    marginBottom: 20,
    width: small ? '100%' : '97%',
}));
